import React, { Fragment, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Grid, File } from 'react-feather';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable from '../shared/ReportTable';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const moneyLoc = (v) => Number(v || 0).toLocaleString();
const money2 = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const atvFmt = (v) => Number(v || 0).toFixed(2);

const styledPct = (v) => {
    const n = Number(v || 0);
    const color = n > 0 ? 'var(--v3-green)' : n < 0 ? 'var(--v3-red)' : undefined;
    const arrow = n > 0 ? '↑' : n < 0 ? '↓' : '';
    return <span style={{ color }}>{money2(n)}% {arrow}</span>;
};

// Date / Sales / Traffic / Revenue / Conversion / ATV.
// Traffic + Conversion are placeholders (no backend data → always 0).
const COMPARE_COLUMNS = [
    { key: 'label', label: 'Date', sortable: false },
    { key: 'sales', label: 'Sales', align: 'right', sortable: false, render: (r) => (r.growth ? styledPct(r.sales) : moneyLoc(r.sales)) },
    { key: 'traffic', label: 'Traffic', align: 'right', sortable: false },
    { key: 'revenue', label: 'Revenue', align: 'right', sortable: false, render: (r) => (r.growth ? styledPct(r.revenue) : moneyLoc(r.revenue)) },
    { key: 'conversion', label: 'Conversion', align: 'right', sortable: false },
    { key: 'atv', label: 'ATV', align: 'right', sortable: false, render: (r) => (r.growth ? styledPct(r.atv) : atvFmt(r.atv)) },
];

const mkRow = (label, sales, revenue, atv, growth = false) => ({ label, sales, traffic: 0, revenue, conversion: '0.00', atv, growth });

const comparisonRows = (d, t) => {
    if (!d || !d.yesterday) return [];
    return [
        mkRow(t('Yesterday'), d.yesterday?.sales, d.yesterday?.revenueShare, d.yesterday?.atv),
        mkRow(t('Last Week'), d.lastWeek?.sales, d.lastWeek?.revenueShare, d.lastWeek?.atv),
        mkRow(t('Last Year'), d.lastYear?.sales, d.lastYear?.revenueShare, d.lastYear?.atv),
        mkRow(t('Yesterday vs LW'), d.growthYesterdayVsLastWeek?.sales, d.growthYesterdayVsLastWeek?.revenueShare, d.growthYesterdayVsLastWeek?.atv, true),
        mkRow(t('Yesterday vs LY'), d.growthYesterdayVsLastYear?.sales, d.growthYesterdayVsLastYear?.revenueShare, d.growthYesterdayVsLastYear?.atv, true),
    ];
};

const propertyRows = (pd, t) => {
    if (!pd) return [];
    return [
        mkRow(t('Yesterday'), pd.yesterday?.total_sales, pd.yesterday?.total_revenue_share, pd.yesterday?.atv),
        mkRow(t('Last Week'), pd.lastWeek?.total_sales, pd.lastWeek?.total_revenue_share, pd.lastWeek?.atv),
        mkRow(t('Last Year'), pd.lastYear?.total_sales, pd.lastYear?.total_revenue_share, pd.lastYear?.atv),
        mkRow(t('Yesterday vs LW'), pd.growthYesterdayVsLastWeek?.sales, pd.growthYesterdayVsLastWeek?.revenueShare, pd.growthYesterdayVsLastWeek?.atv, true),
        mkRow(t('Yesterday vs LY'), pd.growthYesterdayVsLastYear?.sales, pd.growthYesterdayVsLastYear?.revenueShare, pd.growthYesterdayVsLastYear?.atv, true),
    ];
};

const initialFilters = {
    property_id: '',
    selectedFloor: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    tenant_id: '',
};

const DailyComprehensiveRevenue = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);
    const [propertyData, setPropertyData] = useState({});
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;
    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id, concessionaire_id: concessionaireId, module: 'Reports', task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.tenant_id = ''; }
            if (key === 'property_id') { next.selectedFloor = ''; next.tenant_id = ''; }
            if (key === 'subcategory_id') { next.tenant_id = ''; }
            if (key === 'group_name') { next.tenant_id = ''; }
            return next;
        });
        setReportData(null);
        setPropertyData({});
        setHasRun(false);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData(null);
        setPropertyData({});
        setHasRun(false);
        setError('');
    };

    const generateReport = async () => {
        setLoading(true);
        setError('');
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            group_name: filters.group_name,
            tenant_name: nameOf(lookups.tenantOptions, filters.tenant_id),
            floor: filters.selectedFloor,
        };
        try {
            const [cRes, pRes] = await Promise.all([
                axios.post(`${config.hostname}/reports/getdailycomprehensivereport`, payload, { headers }),
                axios.post(`${config.hostname}/reports/getdailycomprehensivebyproperty`, payload, { headers }),
            ]);
            setReportData(cRes.data && typeof cRes.data === 'object' ? cRes.data : null);
            setPropertyData(pRes.data && typeof pRes.data === 'object' ? pRes.data : {});
            setHasRun(true);
            insertToLog(' generated Daily Comprehensive Revenue report');
        } catch (err) {
            console.error('daily comprehensive report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Daily Comprehensive report'));
        } finally {
            setLoading(false);
        }
    };

    const fields = [
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'selectedFloor', label: t('Floor'), type: 'select', options: lookups.floorOptions, placeholder: t('All floors') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'tenant_id', label: t('Tenant'), type: 'select', options: lookups.tenantOptions, placeholder: t('All tenants') },
    ];

    const cmpRows = useMemo(() => comparisonRows(reportData, t), [reportData, t]);
    const propEntries = useMemo(() => Object.entries(propertyData || {}), [propertyData]);

    // ── Exports (preserved, with null-safe formatting) ──────────────
    const metaRows = () => ([
        [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
        [t('Category'), nameOf(lookups.categoryOptions, filters.category_id) || t('All')],
        [t('Subcategory'), nameOf(lookups.subcategoryOptions, filters.subcategory_id) || t('All')],
        [t('Group'), filters.group_name || t('All')],
        [t('Tenant'), nameOf(lookups.tenantOptions, filters.tenant_id) || t('All')],
        [t('Floor'), filters.selectedFloor || t('All')],
    ]);

    const aoaRow = (r) => [r.label, r.growth ? `${money2(r.sales)}%` : money2(r.sales), 0, r.growth ? `${money2(r.revenue)}%` : money2(r.revenue), '0.00', r.growth ? `${money2(r.atv)}%` : money2(r.atv)];

    const exportToExcel = () => {
        if (!hasRun) return;
        const sheet = [];
        metaRows().forEach((m) => sheet.push(m));
        sheet.push([]);
        sheet.push([t('Comparison')]);
        sheet.push([t('Date'), t('Sales'), t('Traffic'), t('Revenue'), t('Conversion'), t('ATV')]);
        cmpRows.forEach((r) => sheet.push(aoaRow(r)));
        sheet.push([]);
        propEntries.forEach(([propName, pd]) => {
            sheet.push([propName]);
            sheet.push([t('Date'), t('Sales'), t('Traffic'), t('Revenue'), t('Conversion'), t('ATV')]);
            propertyRows(pd, t).forEach((r) => sheet.push(aoaRow(r)));
            sheet.push([]);
        });
        const ws = XLSX.utils.aoa_to_sheet(sheet);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, 'DailyComprehensive.xlsx');
        insertToLog(' exported Daily Comprehensive report to excel');
    };

    const exportToPDF = () => {
        if (!hasRun) return;
        const doc = new jsPDF();
        doc.setFontSize(11);
        let y = 12;
        metaRows().forEach(([k, v]) => { doc.text(`${k}: ${v}`, 10, y); y += 6; });
        y += 6;
        doc.text(t('Comparison'), 10, y); y += 4;
        doc.autoTable({ head: [[t('Date'), t('Sales'), t('Traffic'), t('Revenue'), t('Conversion'), t('ATV')]], body: cmpRows.map(aoaRow), startY: y + 2, styles: { fontSize: 8 } });
        y = doc.lastAutoTable.finalY + 10;
        propEntries.forEach(([propName, pd]) => {
            doc.text(propName, 10, y); y += 4;
            doc.autoTable({ head: [[t('Date'), t('Sales'), t('Traffic'), t('Revenue'), t('Conversion'), t('ATV')]], body: propertyRows(pd, t).map(aoaRow), startY: y + 2, styles: { fontSize: 8 } });
            y = doc.lastAutoTable.finalY + 10;
        });
        doc.save('DailyComprehensive.pdf');
        insertToLog(' exported Daily Comprehensive report to pdf');
    };

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Daily Comprehensive Revenue Report')}
                subtitle={t('Yesterday vs last week / last year sales, revenue and ATV, overall and per property.')}
                actions={(
                    <Fragment>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToExcel} disabled={!hasRun} title={t('Export to Excel')}>
                            <Grid size={14} /> {t('Excel')}
                        </button>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToPDF} disabled={!hasRun} title={t('Export to PDF')}>
                            <File size={14} /> {t('PDF')}
                        </button>
                    </Fragment>
                )}
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <h3 className="v3-heading" style={{ margin: '4px 0 0' }}>{t('Comparison')}</h3>
                <ReportTable
                    columns={COMPARE_COLUMNS}
                    rows={cmpRows}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun ? t('No comparison data for the selected filters.') : t('Choose your filters and select Generate report to view results.')}
                />

                {propEntries.map(([propName, pd]) => (
                    <Fragment key={propName}>
                        <h3 className="v3-heading" style={{ margin: '4px 0 0' }}>{propName}</h3>
                        <ReportTable columns={COMPARE_COLUMNS} rows={propertyRows(pd, t)} emptyMessage={t('No data.')} />
                    </Fragment>
                ))}
            </ReportShell>
        </Fragment>
    );
};

export default DailyComprehensiveRevenue;
